// dark_mode.ts - Handle dark mode detection and toggling
// This runs immediately when loaded to avoid FOUC (Flash of Unstyled Content)

export type ThemeMode = 'dark' | 'light' | 'system'

declare global {
  interface Window {
    setDarkMode: (mode: ThemeMode) => void
  }
}

;(function (): void {
  'use strict'

  // Function to check if dark mode should be applied
  function shouldUseDarkMode(): boolean {
    const hasManualTheme = 'theme' in localStorage
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    return localStorage.theme === 'dark' || (!hasManualTheme && systemPrefersDark)
  }

  // Function to apply dark mode class
  function applyDarkMode(): void {
    const shouldBeDark = shouldUseDarkMode()

    if (shouldBeDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  // Apply immediately on script load
  applyDarkMode()

  // Set up listener for system preference changes
  function setupDarkModeListener(): void {
    try {
      const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)')

      const handler = function (_e: MediaQueryListEvent): void {
        // Only auto-update if user hasn't set manual preference
        if (!('theme' in localStorage)) {
          applyDarkMode()
        }
      }

      darkModeQuery.addEventListener('change', handler)
    } catch {
      // Silent fail - dark mode listener not critical
    }
  }

  // Set up listener after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupDarkModeListener)
  } else {
    setupDarkModeListener()
  }

  // Export global function for manual dark mode toggling
  window.setDarkMode = function (mode: ThemeMode): void {
    if (mode === 'dark') {
      localStorage.theme = 'dark'
      document.documentElement.classList.add('dark')
    } else if (mode === 'light') {
      localStorage.theme = 'light'
      document.documentElement.classList.remove('dark')
    } else if (mode === 'system') {
      localStorage.removeItem('theme')
      applyDarkMode()
    }
  }
})()
